import {IDomainObject, DataObjectDto} from "core/form/formUtils";
import {ID, EntityConfig} from "core/metamodel/metaConstant";
import {ObservableMap, observable} from "mobx";
import {MovingBlocks} from "core/grid/gridUtils";
import Form from "core/form/form";
import MdEntity from "core/metamodel/mdEntity";
import mdEntityService from "core/metamodel/mdEntityService";
import {LovItem} from "core/metamodel/mdEntity";
import {ValidationMessage} from "core/common/domain/commonDomain";
import {isArrayLike as isArrayLikeMobx} from "mobx";
import {FieldObjectType} from "core/form/form";

import {DynamicField} from "../../metamodel/metaDomain";


export function entityConfig(clazz?: any, detailRoute?: string) {
  return function (constructor: Function & {entityConfig: EntityConfig}) {
    const entityConfig: EntityConfig = (clazz) ? new clazz() : new EntityConfig(constructor.name);
    entityConfig.entityConstructorFunction = constructor;
    entityConfig.detailroute = detailRoute;
    mdEntityService.setEntityConfig(entityConfig);
  };
}


export function fieldInfo(fieldClazz: any) {
  return function (hostClazz: any, key: string) {
    //console.debug('fieldInfo decorator for %s setting fieldClazz %O on %O', key, fieldClazz, hostClazz);

    if (!fieldClazz) {
      throw new Error("fieldInfo decorator for " + key + " on " + hostClazz.constructor.name + " does not have fieldClazz defined. Check order of Class declaration.");
    }

    if (!hostClazz.constructor.fieldConstructors) hostClazz.constructor.fieldConstructors = {};
    hostClazz.constructor.fieldConstructors[key] =  (fieldClazz.name) ? (o) => fieldClazz : fieldClazz;
  };
}




export abstract class DomainAncestor implements IDomainObject {

  static getInstance<T>(name: string, ...args: any[]) : T {
    let entityConfig = mdEntityService.getEntityConfig(name);
    if (!entityConfig) {
      throw new Error("EntityConfig not found for " + name);
    }
    return <T> new entityConfig.entityConstructorFunction(...args);
  }

  static entityConfig: EntityConfig;

  "@class"?: string;
  $mdEntityRef: MdEntity;

  @observable id: ID;
  @observable $dirty: boolean;
  @observable $new: boolean;
  @observable $added: boolean;
  @observable $loading: boolean;
  @observable $errors: ValidationMessage[];
  @observable $formMap:  ObservableMap<Form<DomainAncestor>>;
  $movingBlocks: MovingBlocks;
  @observable $headerTab: string;


  @observable userCreated: string;
  @observable dateCreated: string|Date;
  @observable dateUpdated: string|Date;
  @observable label: string;
  @observable active: boolean;
  @observable tupleMap: {[key: string]: any};
  @observable tenantId? : number;

  constructor(json?: IDomainObject & {[key: string]: any}) {

    this.$errors = observable.shallowArray<ValidationMessage>();
    this.$formMap = <ObservableMap<Form<DomainAncestor>>> observable.shallowMap();


    this.fromJson(json);

    this.initializeEntityRef();

  }

  /**
   * creates class from json, is used mainly in constructor
   * @param json
   * @param mapFields - list of ObservableMap properties (they need special treatment)
   * @return {DomainAncestor}
   */
  public fromJson(json: any, ...mapFields: string[]): this {
    if (json) {
      for(let key in json) {
        if (json.hasOwnProperty(key)) {
          let rawValue = json[key];
          if (isArrayLikeMobx(rawValue)) {
            this[key] = rawValue.map(js => this.extractValue(key, js));
          } else {
            if (mapFields && mapFields.indexOf(key) !== -1) {
              let mapa = new ObservableMap<any>();
              let mapObject = json[key];
              for(let mapKey in mapObject) {
                if (mapObject.hasOwnProperty(mapKey)) {
                  let mapValue = mapObject[mapKey];
                  mapa.set(mapKey, (isArrayLikeMobx(mapValue)) ? mapValue.map(js => this.extractValue(key, js)) : this.extractValue(key, mapValue))
                }
              }
              this[key] = mapa;
            } else {
              this[key] = this.extractValue(key, json[key]);
            }
          }
        }
      }


    }
    return this;
  }


  public prepareForServer() : this {
    return this;
  }


  public cloneShallow(): this {
    const newObj = new (this.constructor as any)(null);
    for(let key in this) {
      newObj[key] = this[key];
    }
    return newObj;
  }

  public copyStandardFields(newObject: DomainAncestor): void {
    for(let key in newObject) {
      if (!key.startsWith('$')) {
        this[key] = newObject[key];
      }
    }
  }


  public cloneDeep(): this {
    const copyFn = (rawValue: any): any => (rawValue instanceof DomainAncestor) ? rawValue.cloneDeep() : rawValue;
    const newObj = new (this.constructor as any)(null);

    for(let key in this) {
      let rawValue: any = this[key];
      if (isArrayLikeMobx(rawValue)) {
        newObj[key] = rawValue.map(js =>  copyFn(js));
      } else {
        if (rawValue instanceof ObservableMap) {
          let mapa = new ObservableMap<any>();
          for(let entry of rawValue.entries()) {
            mapa.set(entry[0], copyFn(entry[1]) );
          }
          newObj[key] = mapa;
        } else {
          newObj[key] =  copyFn(rawValue);
        }
      }
    }
    return newObj;
  }

  /**
   * Smaze vsechny own properties a nasledne priradi own properties copied objektu
   * @param copied
   * @return void
   */
  public shallowOwnCopyProperties(copied: this): void {
    for(let key in this) {
      if (this.hasOwnProperty(key)) {
        this[key] = null;
      }
    }
    for(let key in copied) {
      if (copied.hasOwnProperty(key)) {
        this[key] = copied[key];
      }
    }
  }

  private extractValue(key: string, jsonValue: any) {
    if (jsonValue) {

      let thisConstructorFn: any = this.constructor as any;
      if (thisConstructorFn.fieldConstructors) {
        let fieldConstructorFn = thisConstructorFn.fieldConstructors[key];
        if (fieldConstructorFn) {
          let fieldConstructor = fieldConstructorFn(this);
          return (jsonValue instanceof fieldConstructor) ? jsonValue : new fieldConstructor(jsonValue);
          //console.log("extractValue key %s value %O, newValue %O", key, jsonValue, newValue);
        }
      }
    }
    return jsonValue;
  }



  public initializeEntityRef() {
    if (this.$mdEntityRef == null) {
      this.$mdEntityRef = mdEntityService.getEntity(this.constructor.name);
      if (this.$mdEntityRef) {
        if (this["@class"] == null) {
          this["@class"] = this.$mdEntityRef.clazzName;
        } else {
          if (this["@class"] !== this.$mdEntityRef.clazzName) {
            throw new Error("@class property " + this["@class"] + " differs from mdEntity's " + this.$mdEntityRef.clazzName + " in " + this.constructor.name);
          }
        }
      }
    }
  }



  public getId(): ID {
    return this.id;
  }

  public setId(id: ID): void {
    this.id = id;
  }


  public hasErrors(): boolean {
    return this.$errors && this.$errors.length > 0;
  }


  public createLov() : LovItem {
    return {
      value: this.getId(),
      label: this.label
    };
  }

  public cleanAfterSave(returningJson: IDomainObject, cleanMovingBlocks = true, ...retainProperties: string[]): boolean {
    const oldNew = this.$new;

    this.$dirty = false;
    this.$new = undefined;
    this.$added = undefined;

    const retainObject = (retainProperties && retainProperties.length) ? retainProperties.reduce((a, e) => (a[e] = this[e], a), {}) : null;

    this.fromJson(returningJson);

    if (retainObject) {
      Object.entries(retainObject).forEach(([k,v]) => this[k] = v);
    }

    if (cleanMovingBlocks && this.$movingBlocks) {
      this.$movingBlocks.cleanAfterSave(this);
    }

    if (this.$formMap) {
      for(let form of this.$formMap.values()) {
        form.updateAllFieldsPropValue(this);
      }
    }

    return oldNew;
  }

  public cleanOnCopy(): void {
    this.$new = true;
    this.$dirty = true;
    this.userCreated = undefined;
    this.dateCreated = undefined;
    this.dateUpdated = undefined;

    this.setId(null);
  }

  public initAfterRead(isNew: boolean): this {
    this.initializeEntityRef();
    if (isNew) {
      this.initDefaultValues();
      this.$dirty = true;
    }
    return this;
  }



  public getForm(formName: string) : Form<this> {
    let form: Form<this> = <Form<this>> this.$formMap.get(formName);
    if (!form) {
      form = new Form<this>(formName, mdEntityService.getFormDefinition(formName));
      this.$formMap.set(formName, form);
    }
    return form;
  }

  public getFormFields(formName: string) : {[key in keyof this]: FieldObjectType} {
    let form: Form<this> = this.getForm(formName);
    return form.fields;
  }


  public getFormField(formName: string, fieldName: keyof this) : FieldObjectType {
    let form: Form<this> = this.getForm(formName);
    return form.getField(fieldName);
  }


  public initDefaultValues(): this {
    this.initializeEntityRef();

    // inicializace default hodnot
    for(let mdField of this.$mdEntityRef.getFields()) {
      let defaultValue: string = mdField.defaultValue;
      if (defaultValue) {
        const that = this;  // saby this bylo dostupne v eval, tedy je tam dustupne jako that
        if (defaultValue.startsWith('@')) {
          eval(defaultValue.substring(1));
        } else if (this[mdField.fieldName] == null) {
          this[mdField.fieldName] = mdField.computeDefaultValue();
        }
      }

    }

    // dynamic fields
    const dynFields = this["fields"] as DynamicField[];
    if (dynFields) {
      dynFields.forEach(df => {
        df.value = df.$mdFieldRef.computeDefaultValue()
      });
    }

    return this;
  }

  public createDataObjectDto(): DataObjectDto<this> {
    return {
      dataObject: this as any,
      gridData: (this.$movingBlocks) ? this.$movingBlocks.getGridData() : {}
    };
  }


  public getTuple(name: string): any {
    return (this.tupleMap) ? this.tupleMap[name] : null;
  }

  public setTuple(name: string, value: any) : void {
    if (!this.tupleMap) this.tupleMap = {};
    this.tupleMap[name] = value;
  }

}



// WEBPACK FOOTER //
// ./src/core/common/domain/DomainAncestor.ts